import { EntityType } from '@frontend/common';
import { Logger } from '@frontend/logger';
import { notifyEntityCreated } from '@frontend/repository';
import { SettingKey } from '@frontend/setting/types';
import { TransactionClient, TransactionExtendedClient } from '@frontend/transaction/api';
import {
    ExtendedPudoTransaction,
    ExtendedTransaction,
    ExtendedVendingTransaction,
    Transaction,
    TransactionStateName,
    TransactionType
} from '@frontend/transaction/types';
import { MobileWizardStateRecord, updateData } from '@frontend/wizard/mobile';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '../../redux/store';

type AnyTransaction = Transaction | ExtendedTransaction | ExtendedPudoTransaction | ExtendedVendingTransaction;

/**
 * Transaction synchronization states:
 * - INIT: Transaction exists locally but not yet created on backend
 * - AHEAD: Local changes exist that haven't been synced to backend
 * - SYNCED: Local and backend are in sync
 */
export enum TransactionSyncState {
    INIT = 'init',
    AHEAD = 'ahead',
    SYNCED = 'synced'
}

export interface UseWizardTransactionOptions {
    wizardId?: string;
    store: MobileWizardStateRecord | null;

    /**
     * Auto-create transaction on mount if conditions are met
     * @default false
     */
    autoCreate?: boolean;

    /**
     * Auto-fetch transaction from URL params on mount
     * @default true
     */
    autoFetch?: boolean;

    /**
     * Callback fired when transaction is created
     */
    onCreate?: (transaction: AnyTransaction) => void | Promise<void>;

    /**
     * Callback fired when leaving screen/unmounting
     * Return false to prevent default cleanup behavior
     */
    onReturn?: () => boolean | Promise<boolean>;

    /**
     * Additional query params to look for transaction ID
     * @default ['transaction_id']
     */
    transactionIdParams?: string[];
}

export interface UseWizardTransactionReturn {
    /** Current transaction data (merged local + backend) */
    transaction: Partial<AnyTransaction> | null;
    remoteTransaction: AnyTransaction | null;

    /** Loading state for async operations */
    isLoading: boolean;

    /** Current synchronization state */
    transactionState: TransactionSyncState;

    /**
     * Update transaction data locally (in Redux store)
     * Does not sync to backend immediately
     */
    localUpdateTransaction: (data: Partial<AnyTransaction>) => Promise<void>;

    /**
     * Create transaction on backend if in INIT state, has required data and doesn't exist yet
     * Update transaction on backend if in AHEAD state
     * Transitions state from INIT|AHEAD to SYNCED
     *
     * @param data - Optional transaction data to include/override during creation.
     *               During creation, you can override the default feature from store.functionality.feature
     *               by passing feature in this data object. After creation, feature cannot be changed.
     */
    syncTransaction: (data?: Partial<AnyTransaction>) => Promise<AnyTransaction>;

    /**
     * Refresh transaction data from backend
     * Overwrites local changes
     */
    refresh: () => Promise<void>;

    clearData: () => void;
}

/**
 * Hook for managing wizard transaction lifecycle and synchronization
 */
export const useWizardTransaction = (options: UseWizardTransactionOptions): UseWizardTransactionReturn => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const { state: locationState } = useLocation();

    const [isLoading, setIsLoading] = useState(false);
    const [transactionState, setTransactionState] = useState<TransactionSyncState>(TransactionSyncState.INIT);
    const [remoteTransaction, setRemoteTransaction] = useState<AnyTransaction | null>(null);

    // Track if component is mounted to prevent state updates after unmount
    const isMountedRef = useRef(true);
    const cleanupExecutedRef = useRef(false);
    const isCreatingRef = useRef(false);

    const { wizardId, store, autoCreate = false, autoFetch = true, transactionIdParams = ['transaction_id'] } = options;
    const localTransaction = store?.data?.transaction as AnyTransaction | undefined;

    useEffect(() => {
        if (wizardId && !localTransaction && locationState?.transaction) {
            dispatch(
                updateData({
                    key: wizardId,
                    data: { transaction: locationState.transaction }
                })
            );
        }
    }, []);

    // Store ref to access latest store state
    const storeRef = useRef(store);
    useEffect(() => {
        storeRef.current = store;
    }, [store]);

    /**
     * Helper to wait for store update to be reflected
     */
    const waitForStoreUpdate = async (checkFn: () => boolean, maxAttempts = 50, delayMs = 10) => {
        for (let i = 0; i < maxAttempts; i++) {
            if (checkFn()) {
                return true;
            }
            await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
        return false;
    };

    /**
     * Initialize transaction from URL params or wizard store
     */
    useEffect(() => {
        if (!autoFetch || !store || !store.wizard.account_id || !wizardId) return;

        const initializeTransaction = async () => {
            //check if there is a transaction known
            let transactionId = undefined;
            if (localTransaction?.id) {
                transactionId = localTransaction.id;
            } else {
                // Look for transaction_id in URL params
                transactionId = transactionIdParams.map((param) => searchParams.get(param)).find((id) => id !== null);
            }

            if (transactionId) {
                setIsLoading(true);
                try {
                    const fetchedTransaction = await TransactionExtendedClient.fetchExtendedTransaction(store.wizard.account_id, transactionId);
                    setRemoteTransaction(fetchedTransaction);

                    //Store fetched transaction if there are no local changes
                    if (fetchedTransaction && isMountedRef.current && !localTransaction) {
                        // Store in wizard data
                        dispatch(
                            updateData({
                                key: wizardId,
                                data: { transaction: fetchedTransaction }
                            })
                        );
                        setTransactionState(TransactionSyncState.SYNCED);
                    } else {
                        // Compare fetched transaction with local wizard transaction
                        // Check each key to determine if local changes exist
                        let hasLocalChanges = false;

                        if (localTransaction) {
                            for (const key in localTransaction) {
                                if (Object.prototype.hasOwnProperty.call(localTransaction, key)) {
                                    const localValue = localTransaction[key as keyof AnyTransaction];
                                    const remoteValue = fetchedTransaction[key as keyof AnyTransaction];

                                    // Deep comparison for objects/arrays, shallow for primitives
                                    if (JSON.stringify(localValue) !== JSON.stringify(remoteValue)) {
                                        hasLocalChanges = true;
                                        break;
                                    }
                                }
                            }
                        }

                        if (hasLocalChanges) {
                            setTransactionState(TransactionSyncState.AHEAD);
                        } else {
                            setTransactionState(TransactionSyncState.SYNCED);
                        }
                    }
                } catch (error) {
                    Logger.error('Failed to fetch transaction:', error);
                } finally {
                    if (isMountedRef.current) {
                        setIsLoading(false);
                    }
                }
            } else setTransactionState(TransactionSyncState.INIT);
        };

        initializeTransaction();
    }, [store, wizardId, autoFetch]);

    /**
     * Auto-create transaction if enabled
     */
    useEffect(() => {
        if (!autoCreate || !store || transactionState !== TransactionSyncState.INIT || isCreatingRef.current) return;

        let transactionId = undefined;
        if (localTransaction?.id) {
            transactionId = localTransaction.id;
        } else {
            // Look for transaction_id in URL params
            transactionId = transactionIdParams.map((param) => searchParams.get(param)).find((id) => id !== null);
        }

        if (transactionId) return; // Already has transaction

        const shouldCreate = hasRequiredDataForCreation(store);
        if (shouldCreate) {
            isCreatingRef.current = true;
            syncTransaction().finally(() => {
                isCreatingRef.current = false;
            });
        }
    }, [autoCreate, store, transactionState]);

    /**
     * Cleanup on unmount
     *
     */
    useEffect(() => {
        return () => {
            isMountedRef.current = false;

            if (cleanupExecutedRef.current) return;
            cleanupExecutedRef.current = true;

            // Execute cleanup logic
            const cleanup = async () => {
                if (options.onReturn) {
                    const shouldProceed = await options.onReturn();
                    if (shouldProceed === false) return;
                }
            };

            cleanup();
        };
    }, [transactionState, options.onReturn]);

    /**
     * Update transaction data locally (in Redux store only)
     */
    const localUpdateTransaction = async (data: Partial<AnyTransaction>, synced?: boolean) => {
        if (!store || !wizardId) {
            Logger.warn('Cannot update transaction: wizard store not available');
            return;
        }

        const newTransactionData = {
            ...(localTransaction || {}),
            ...data
        };

        // Update wizard store
        await dispatch(
            updateData({
                key: wizardId,
                data: {
                    transaction: newTransactionData
                }
            })
        );

        // Mark as ahead if transaction exists on backend
        if (transactionState === TransactionSyncState.SYNCED && synced !== true) {
            setTransactionState(TransactionSyncState.AHEAD);
        }

        // Wait for the store to actually reflect the change
        const updated = await waitForStoreUpdate(() => {
            const currentStore = storeRef.current;
            const currentTransaction = currentStore?.data?.transaction as AnyTransaction | undefined;
            // Check if key properties are updated (especially the ID)
            if (data.id && currentTransaction?.id !== data.id) return false;
            if (data.spot_id && currentTransaction?.spot_id !== data.spot_id) return false;
            if (data.slot_id && currentTransaction?.slot_id !== data.slot_id) return false;
            return true;
        });

        if (!updated) {
            Logger.warn('Store update timeout: transaction may not have been updated yet');
        }
    };

    const syncTransaction = async (data?: Partial<AnyTransaction>): Promise<AnyTransaction> => {
        if (!store) {
            throw new Error('Cannot create or update transaction: wizard store not available');
        }
        let transaction = remoteTransaction;

        if (!remoteTransaction) {
            //create
            if (!hasRequiredDataForCreation(store)) {
                throw new Error('Cannot create transaction: missing required data (workflow_id, functionality)');
            }
            setIsLoading(true);
            try {
                const wfIdSetting = store.functionalitySettings.find((s) => s.key === 'transaction_workflow_id');

                const newTransaction = await TransactionClient.postTransaction(store.wizard.account_id, {
                    application: store.functionality.application,
                    feature: store.functionality.feature, // Default from store, can be overridden by localTransaction or data parameter
                    state: TransactionStateName.TRANSACTION_DRAFT,
                    workflow_id: wfIdSetting!.value as string,
                    type: TransactionType.PUDO, // TODO: For now default to PUDO, make configurable later
                    ...localTransaction,
                    ...data
                });
                notifyEntityCreated(dispatch, EntityType.TRANSACTION);

                transaction = await TransactionExtendedClient.fetchExtendedTransaction(store.wizard.account_id, newTransaction.id);
                // Store in wizard data
                localUpdateTransaction(transaction, true);
                setRemoteTransaction(transaction);
                setTransactionState(TransactionSyncState.SYNCED);

                // Call onCreate callback
                if (options.onCreate) {
                    await options.onCreate(transaction);
                }
            } catch (error) {
                Logger.error('Failed to create transaction:', error);
                throw error;
            } finally {
                if (isMountedRef.current) {
                    setIsLoading(false);
                }
            }
        } else {
            //update
            setIsLoading(true);
            try {
                const updatedTransaction = await TransactionClient.patchTransaction(remoteTransaction.account_id, remoteTransaction.id, {
                    ...localTransaction,
                    ...data
                });

                // Refresh to get updated transaction with relations
                const extendedTransaction = await TransactionExtendedClient.fetchExtendedTransaction(remoteTransaction.account_id, updatedTransaction.id);

                transaction = extendedTransaction;
                if (isMountedRef.current) {
                    localUpdateTransaction(extendedTransaction, true);
                    setRemoteTransaction(extendedTransaction);
                    setTransactionState(TransactionSyncState.SYNCED);
                }
            } catch (error) {
                Logger.error('Failed to update transaction:', error);
                throw error;
            } finally {
                if (isMountedRef.current) {
                    setIsLoading(false);
                }
            }
        }

        return transaction;
    };

    /**
     * Refresh transaction from backend
     */
    const refresh = async (): Promise<void> => {
        if (!remoteTransaction?.id) {
            throw new Error('Cannot refresh: no transaction ID');
        }

        setIsLoading(true);
        try {
            const refreshedTransaction = await TransactionExtendedClient.fetchExtendedTransaction(remoteTransaction.account_id, remoteTransaction.id);

            if (refreshedTransaction && isMountedRef.current) {
                localUpdateTransaction(refreshedTransaction, true);
                setRemoteTransaction(refreshedTransaction);

                setTransactionState(TransactionSyncState.SYNCED);
            }
        } catch (error) {
            Logger.error('Failed to refresh transaction:', error);
            throw error;
        } finally {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    };

    const clearData = () => {
        if (!store || !wizardId) {
            Logger.warn('Cannot clear transaction: wizard store not available');
            return;
        }

        dispatch(
            updateData({
                key: wizardId,
                data: {
                    transaction: undefined
                }
            })
        );
        setRemoteTransaction(null);
        setTransactionState(TransactionSyncState.INIT);
    };

    return {
        transaction: localTransaction ?? null,
        remoteTransaction,
        isLoading,
        transactionState,
        localUpdateTransaction,
        syncTransaction,
        refresh,
        clearData
    };
};

/**
 * Check if wizard has required data to create a transaction
 */
function hasRequiredDataForCreation(store: MobileWizardStateRecord): boolean {
    const wfIdSetting = store.functionalitySettings.find((s) => s.key === SettingKey.TRANSACTION_WORKFLOW_ID);
    return !!(wfIdSetting?.value && typeof wfIdSetting.value === 'string' && store.functionality?.application && store.functionality?.feature);
}

export function transactionToTransactionExtended(transaction: Transaction): ExtendedTransaction {
    return {
        ...transaction,
        senders: null,
        receivers: null,
        spot: null,
        slot: null,
        pin_codes: null
    };
}
